import { useState, useEffect, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Coins, Play, Sparkles, ChevronRight } from "lucide-react";
import MonetagVignette from "@/components/MonetagVignette";

const APPLIXIR_API_KEY = "5536bc48-2d2a-4c1f-9083-d01079f98f41";
const APPLIXIR_CONTAINER_ID = "applixir-ad-container";

const words = [
  "Great job! 👏", "Amazing! 🌟", "Keep going! 💪", "Fantastic! 🎉",
  "Nice! ✨", "Wonderful! 💖", "Superb! 🎈", "You rock! 🤘",
  "Bravo! 👑", "Incredible! 🚀",
  "Stay hungry, stay foolish. 🍎",
  "Be yourself; everyone else is already taken. 🎭",
  "The only way out is through. 🚪",
  "What we think, we become. 🌱",
  "In the middle of difficulty lies opportunity. 💎",
  "Do what you can, with what you have, where you are. 📍",
  "It is never too late to be what you might have been. ⏳",
  "Not all those who wander are lost. 🧭",
  "Wherever you go, there you are. 🌍",
  "This too shall pass. 🌊"
];

const balloonColors = [
  "radial-gradient(circle at 30% 30%, #FF6B6B, #EE3B3B)",
  "radial-gradient(circle at 30% 30%, #6BCB77, #2E8B57)",
  "radial-gradient(circle at 30% 30%, #87CEEB, #4169E1)",
  "radial-gradient(circle at 30% 30%, #FFD700, #FF8C00)",
  "radial-gradient(circle at 30% 30%, #DA70D6, #8B008B)",
  "radial-gradient(circle at 30% 30%, #FF69B4, #C71585)",
];

const ROWS = 4;
const COLS = 4;
const TOKEN_REWARD_POPS = 50;
const AD_EVERY_POPS = 10;

if (!document.getElementById("balloon-styles")) {
  const style = document.createElement("style");
  style.id = "balloon-styles";
  style.innerHTML = `
    @keyframes popBalloon {
      0% { transform: scale(1); opacity: 1; }
      50% { transform: scale(1.3); opacity: 0.5; }
      100% { transform: scale(0); opacity: 0; }
    }
    @keyframes floatWord {
      0% { transform: translateY(0) scale(1); opacity: 1; }
      80% { opacity: 1; }
      100% { transform: translateY(-120px) scale(1.3); opacity: 0; }
    }
    .pop-animation {
      animation: popBalloon 0.3s ease-out forwards;
    }
    .float-word-animation {
      animation: floatWord 2s ease-out forwards;
    }
  `;
  document.head.appendChild(style);
}

export default function BalloonGame() {
  const { user, refresh, setUser } = useAuth();
  const [score, setScore] = useState(0);
  const [balloons, setBalloons] = useState([]);
  const [floatingWords, setFloatingWords] = useState([]);
  const [poppingId, setPoppingId] = useState(null);
  const lastRewardedScore = useRef(0);
  const adInProgress = useRef(false);
  const adContainerRef = useRef(null);

  const isPremium = user?.premium_tier && user?.premium_expires_at && new Date(user.premium_expires_at) > new Date();

  const showForcedAd = useCallback(() => {
    if (adInProgress.current) return;
    if (!window.initializeAndOpenPlayer) return;
    adInProgress.current = true;
    window.initializeAndOpenPlayer({
      apiKey: APPLIXIR_API_KEY,
      injectionElementId: APPLIXIR_CONTAINER_ID,
      adStatusCallbackFn: () => { adInProgress.current = false; },
      adErrorCallbackFn: () => { adInProgress.current = false; },
    });
  }, []);

  const showRewardedAd = useCallback(() => {
    if (adInProgress.current) return;
    if (!window.initializeAndOpenPlayer) {
      toast.error("Ad network not available – try again later");
      return;
    }
    adInProgress.current = true;
    window.initializeAndOpenPlayer({
      apiKey: APPLIXIR_API_KEY,
      injectionElementId: APPLIXIR_CONTAINER_ID,
      adStatusCallbackFn: (status) => {
        if (status.type === "complete") {
          api.post("/earn-tokens-ad")
            .then(res => {
              setUser(prev => ({ ...prev, tokens: res.data.total_tokens }));
              toast.success("Earned 2 tokens! 🪙");
            })
            .catch(() => {});
        }
        adInProgress.current = false;
      },
      adErrorCallbackFn: () => {
        adInProgress.current = false;
        toast.error("Ad didn't load – keep popping!");
      },
    });
  }, [setUser]);

  useEffect(() => {
    if (!document.getElementById(APPLIXIR_CONTAINER_ID)) {
      const div = document.createElement("div");
      div.id = APPLIXIR_CONTAINER_ID;
      div.style.position = "fixed";
      div.style.top = "0";
      div.style.left = "0";
      div.style.width = "100%";
      div.style.height = "100%";
      div.style.zIndex = "9999";
      div.style.pointerEvents = "none";
      document.body.appendChild(div);
      adContainerRef.current = div;
    }
    return () => {
      if (adContainerRef.current) {
        adContainerRef.current.remove();
        adContainerRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    const initial = [];
    for (let r = 0; r < ROWS; r++) {
      for (let c = 0; c < COLS; c++) {
        initial.push({
          id: `${r}-${c}-${Date.now()}-${Math.random()}`,
          row: r,
          col: c,
          color: balloonColors[Math.floor(Math.random() * balloonColors.length)],
          popped: false,
        });
      }
    }
    setBalloons(initial);
  }, []);

  const handleEarnTokens = useCallback(async () => {
    try {
      const res = await api.post("/earn-tokens");
      setUser(prev => ({ ...prev, tokens: res.data.total_tokens }));
      await refresh();
      toast.success(`You earned ${res.data.tokens_awarded} tokens! 🪙`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not earn tokens");
    }
  }, [setUser, refresh]);

  useEffect(() => {
    if (score > 0 && score % TOKEN_REWARD_POPS === 0 && !isPremium && lastRewardedScore.current !== score) {
      lastRewardedScore.current = score;
      handleEarnTokens();
    }
  }, [score, isPremium, handleEarnTokens]);

  const popBalloon = useCallback((id) => {
    if (adInProgress.current) return;
    setBalloons(prev => {
      const balloon = prev.find(b => b.id === id);
      if (!balloon || balloon.popped) return prev;

      setPoppingId(id);
      setTimeout(() => setPoppingId(null), 300);

      const wordText = words[Math.floor(Math.random() * words.length)];
      const wordId = Date.now() + Math.random();
      setFloatingWords(prevWords => [
        ...prevWords,
        { id: wordId, text: wordText, row: balloon.row, col: balloon.col }
      ]);
      setTimeout(() => {
        setFloatingWords(prevWords => prevWords.filter(w => w.id !== wordId));
      }, 2000);

      setScore(s => {
        const newScore = s + 1;
        if (newScore % AD_EVERY_POPS === 0 && !isPremium) {
          showForcedAd();
        }
        return newScore;
      });

      setTimeout(() => {
        setBalloons(currentBalloons =>
          currentBalloons.map(b => {
            if (b.id === id) {
              return {
                ...b,
                popped: false,
                color: balloonColors[Math.floor(Math.random() * balloonColors.length)],
                id: `${b.row}-${b.col}-${Date.now()}-${Math.random()}`,
              };
            }
            return b;
          })
        );
      }, 500);

      return prev.map(b => b.id === id ? { ...b, popped: true } : b);
    });
  }, [isPremium, showForcedAd]);

  const popsUntilReward = TOKEN_REWARD_POPS - (score % TOKEN_REWARD_POPS);
  const adTriggerCountdown = AD_EVERY_POPS - (score % AD_EVERY_POPS);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-100 via-pink-50 to-yellow-100">
      <Navbar />

      <div className="max-w-xl mx-auto px-4 py-2 space-y-2">

        {/* Share-for-100-tokens banner */}
        <Link
          to="/earn-tokens/share"
          className="block bg-gradient-to-r from-purple-600 to-pink-500 text-white rounded-2xl px-4 py-3 shadow-md hover:opacity-95 transition-opacity"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-white/20 backdrop-blur flex items-center justify-center flex-shrink-0">
              <Sparkles className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-sm">
                Want 100 tokens instead?
              </div>
              <div className="text-xs text-white/80">
                Share Haven on social media — verified within 24 hours.
              </div>
            </div>
            <ChevronRight className="w-4 h-4 flex-shrink-0" />
          </div>
        </Link>

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3 bg-white/90 backdrop-blur-sm rounded-full px-4 py-1.5 shadow-md">
            <span className="font-bold text-lg">Score: {score}</span>
            <div className="w-px h-5 bg-gray-300" />
            {isPremium ? (
              <span className="text-xs text-purple-600">Premium – no ads</span>
            ) : (
              <>
                <div className="flex items-center gap-1 text-sm">
                  <Coins className="w-4 h-4 text-amber-500" />
                  <span>{popsUntilReward}</span>
                </div>
                <div className="w-px h-5 bg-gray-300" />
                <span className="text-xs text-gray-500">Ad in {adTriggerCountdown}</span>
              </>
            )}
          </div>
        </div>

        {!isPremium && (
          <div className="flex justify-center">
            <button
              onClick={showRewardedAd}
              disabled={adInProgress.current}
              className="text-sm font-medium bg-purple-600 text-white border border-purple-700 px-5 py-2.5 rounded-full flex items-center gap-2 hover:bg-purple-700 active:scale-95 transition-all disabled:opacity-50 shadow-lg"
            >
              <Play className="w-4 h-4" />
              Watch Ad for 2 Tokens
            </button>
          </div>
        )}

        <div className="flex justify-center">
          <div className="grid grid-cols-4 gap-4 justify-items-center">
            {balloons.map((balloon) => (
              <div
                key={balloon.id}
                className="relative cursor-pointer select-none"
                onClick={() => popBalloon(balloon.id)}
              >
                <div
                  className={`w-16 h-20 rounded-full flex items-center justify-center transition-transform hover:scale-110 ${
                    poppingId === balloon.id ? "pop-animation" : ""
                  }`}
                  style={{
                    background: balloon.color,
                    visibility: balloon.popped && poppingId !== balloon.id ? "hidden" : "visible",
                  }}
                >
                  <div className="absolute top-2 left-4 w-2 h-2 bg-white/60 rounded-full" />
                  <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-3 h-3 bg-gray-500 rounded-full" />
                  <div className="absolute -bottom-10 left-1/2 -translate-x-1/2 w-px h-8 bg-gray-400" />
                </div>
                {floatingWords
                  .filter(w => w.row === balloon.row && w.col === balloon.col)
                  .map(w => (
                    <div
                      key={w.id}
                      className="absolute top-0 left-1/2 -translate-x-1/2 text-sm font-bold text-purple-700 whitespace-nowrap pointer-events-none float-word-animation"
                    >
                      {w.text}
                    </div>
                  ))}
              </div>
            ))}
          </div>
        </div>

        <MonetagVignette className="mt-2 w-full" style={{ minHeight: "250px" }} />
      </div>

      {score > 0 && score % TOKEN_REWARD_POPS === 0 && !isPremium && (
        <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-40 bg-white/90 backdrop-blur-sm rounded-2xl px-8 py-6 shadow-2xl animate-fade-in">
          <p className="text-2xl font-display font-bold text-center mb-2">+15 Tokens! 🪙</p>
          <p className="text-gray-600 text-center">Great job! You reached {score} points!</p>
        </div>
      )}
    </div>
  );
}